import type { ComponentProps, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { withRouter } from 'react-router-dom';

import type { SxProps, Theme } from '@mui/material';
import { Box, Button as MuiButton, Typography } from '@mui/material';

import { withAPIService } from '../../../../networking';
import { withStore } from '../../../../store';
import { clearAllAttachments } from '../../../../store/attachments';
import { updateSessionStorage } from '../../../../utils/storage';
import type { CardApiService, CardDispatch } from '../../types';

const CARD_PADDING = {
  xs: '8px 0px 8px 0px',
  sm: 'var(--padding-padding-base, 16px) var(--spacing-spacing-4xl, 32px) var(--padding-padding-xl, 8px) var(--padding-padding-lg, 0)'
};

const INFO_COLUMN_HALF_SX = {
  flex: { xs: '0 0 calc(50% - 8px)', sm: 1 },
  maxWidth: { xs: 'calc(50% - 8px)', sm: 'none' },
  minWidth: { xs: 0, sm: 200 }
};

const START_BUTTON_BASE_SX = {
  fontWeight: 700,
  px: 2.5,
  py: 1
};

interface StartButtonProps extends ComponentProps<typeof MuiButton> {
  sx?: SxProps<Theme>;
  children?: ReactNode;
}

// Start button using MUI for consistency
export const StartButton = ({ sx, children, ...props }: StartButtonProps) => (
  <MuiButton variant="contained" color="primary" sx={{ ...START_BUTTON_BASE_SX, ...(sx || {}) }} {...props}>
    {children}
  </MuiButton>
);

interface RequestAddress {
  address?: string;
  name?: string;
}

interface RequestCardProps {
  history?: { push: (path: string) => void };
  address?: RequestAddress;
  recordType?: string;
  id?: string;
  dispatch?: CardDispatch;
  apiService?: CardApiService;
}

export function RequestCard({
  history,
  address: { address = 'NA', name = 'NA' } = {},
  recordType = 'customersetup',
  id,
  dispatch,
  apiService
}: RequestCardProps) {
  const { t } = useTranslation();
  const startOnboardingForm = async (hcId?: string) => {
    const existingHcId = updateSessionStorage.getItem('hcId');
    if (existingHcId !== hcId) {
      dispatch?.(clearAllAttachments());
      updateSessionStorage.setItem('hcId', hcId);
      await apiService.getCustomerDetails(dispatch);
    }
    history?.push('/secure/form');
    // return <Redirect to="/secure/form" />;
  };

  const formattedRecordType = recordType.toLowerCase().replace(/\s/g, '');

  // Body content suitable for AccordionDetails
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--padding-padding-lg, 16px)',
        p: CARD_PADDING
      }}
    >
      <Typography variant="body1" sx={{ fontWeight: 400 }}>
        {t(`dashboard.requestDesc.${formattedRecordType}`)}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          rowGap: 2,
          columnGap: 2
        }}
      >
        <Box sx={INFO_COLUMN_HALF_SX}>
          <Typography variant="body2" sx={{ color: '#707773', fontWeight: 700 }}>
            {t('dashboard.actions.name', 'Name')}
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 700 }}>
            {name ? name : <span>--</span>}
          </Typography>
        </Box>
        <Box sx={INFO_COLUMN_HALF_SX}>
          <Typography variant="body2" sx={{ color: '#707773', fontWeight: 700 }}>
            {t('dashboard.actions.address', 'Address')}
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 700 }}>
            {address ? address : <span>--</span>}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: { xs: '100%', sm: 'auto' }
          }}
        >
          <StartButton sx={{ width: { xs: '100%', sm: 'auto' } }} onClick={() => startOnboardingForm(id)}>
            {t('dashboard.actions.start', 'Start')}
          </StartButton>
        </Box>
      </Box>
    </Box>
  );
}

export default withStore(withAPIService(withRouter(RequestCard)));
